import el from "@cypherpotato/el";
import { InlineCode, MutedText, Paragraph } from "./text";
import { NumberInput } from "./form";
import { throttledCall } from "../ui/throttle";
import { CodeEditor } from "./code-editor";
import { Section, SmallContainer } from "./layout";
import { SpecializedControl } from "./specializedObject";
import { Button } from "./button";
import { cond } from "../util";

function generatePermutations(e, count) {
    if (!Number.isInteger(count) || count < 0) {
        throw new TypeError("count should be > 0.");
    }

    const expression = e.replace(/[\s\n]+/g, ' ');
    const variablePattern = /\{([^{}]+)\}/g;

    function parseOptions(content) {
        return content.split("|").map(rawOption => {
            const option = rawOption.trim();

            // Only interprets ':number' at the end as weight.
            const match = option.match(/^(.*?)(?::\s*(\d+(?:\.\d+)?))?$/);

            const text = match[1].trim();
            const weight = match[2] === undefined
                ? 1
                : Number(match[2]);

            if (!text) {
                throw new Error(`Empty option found: {${content}}`);
            }

            if (!Number.isFinite(weight) || weight <= 0) {
                throw new Error(
                    `Invalid weight for "${text}": ${match[2]}`
                );
            }

            return { text, weight };
        });
    }

    function selectWeighted(options) {
        const totalWeight = options.reduce(
            (total, option) => total + option.weight,
            0
        );

        let random = Math.random() * totalWeight;

        for (const option of options) {
            random -= option.weight;

            if (random < 0) {
                return option.text;
            }
        }

        // Protection against floating point imprecisions.
        return options.at(-1).text;
    }

    // Parse only once.
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = variablePattern.exec(expression)) !== null) {
        parts.push({
            type: "text",
            value: expression.slice(lastIndex, match.index)
        });

        parts.push({
            type: "options",
            value: parseOptions(match[1])
        });

        lastIndex = variablePattern.lastIndex;
    }

    parts.push({
        type: "text",
        value: expression.slice(lastIndex)
    });

    return Array.from({ length: count }, () =>
        parts
            .map(part =>
                part.type === "text"
                    ? part.value
                    : selectWeighted(part.value)
            )
            .join("")
    );
}

function generatePermutationsOrFail(expression, count) {
    try {
        return generatePermutations(expression, count).join('\n');
    } catch (ex) {
        return ex.message;
    }
}

function calculateDuplicateInfo(expression, count) {
    const variablePattern = /\{([^{}]+)\}/g;
    let totalEntropy = 0;
    let hasVariables = false;
    let maxUnique = 1;
    let match;

    while ((match = variablePattern.exec(expression)) !== null) {
        hasVariables = true;
        const options = match[1].split("|");
        maxUnique *= options.length;

        const weights = options.map(raw => {
            const weightMatch = raw.trim().match(/^(.*?)(?::\s*(\d+(?:\.\d+)?))?$/);
            return Number(weightMatch?.[2] ?? 1);
        });

        const totalWeight = weights.reduce((sum, w) => sum + w, 0);
        for (const w of weights) {
            const p = w / totalWeight;
            if (p > 0) totalEntropy -= p * Math.log2(p);
        }
    }

    const effectivePermutations = Math.pow(2, totalEntropy);

    let probability;
    if (!hasVariables) {
        probability = count > 1 ? 1 : 0;
    } else if (count <= 1) {
        probability = 0;
    } else {
        probability = 1 - Math.exp(-(count * (count - 1)) / (2 * effectivePermutations));
    }

    return { effectivePermutations, probability, maxUnique };
}

export function PermutationGenerator() {

    const script = el.state.create("Hello, { world | AIVAX:2 }!");
    const generationAmount = el.state.create(10);
    var forceNonDuplicatedOutput = false;

    return el(".permutation-generator[form-slot]",
        {
            $properties: {

                value: {
                    get() {
                        if (forceNonDuplicatedOutput) {
                            const results = generatePermutations(script.value, generationAmount.value);
                            return [...new Set(results)].join('\n');
                        }
                        return generatePermutationsOrFail(script.value, generationAmount.value);
                    }
                },
            }
        },
        Section(
            Paragraph("You are generating a list of permutations from the expression below."),
            Paragraph("You can define substitution groups using weighted markers, example: ", InlineCode(`{ blue:0.25 | green:2 }`))
        ),
        Section(
            CodeEditor({
                value: script.value,
                language: "spintax",
                height: "8em",
                props: {
                    onInput(event) {
                        throttledCall(() => {
                            script.value = event.target.value;
                        }, 25);
                    }
                }
            })
        ),
        Section(
            el.state.listening([script], () =>
                CodeEditor({
                    value: generatePermutationsOrFail(script.value, 5),
                    editorProps: {
                        readonly: true
                    },
                    wrapperProps: {
                        onClick() {
                            this.value = generatePermutationsOrFail(script.value, 5)
                        }
                    }
                })
            )
        ),
        Section(
            SmallContainer(
                SpecializedControl({
                    fields: [
                        {
                            name: "generation_size",
                            label: "Generation size",
                            control: NumberInput({
                                step: 100,
                                min: 1,
                                max: 100_000,
                                onChange(value) {
                                    generationAmount.value = value;
                                    forceNonDuplicatedOutput = false;
                                }
                            })
                        },
                        {
                            name: "safe_maximum",
                            label: "Safe maximum",
                            control: Button("Use safe maximum", {
                                title: "Sets generation size to the maximum number of unique permutations, guaranteeing no duplicates.",
                                onClick() {
                                    const { maxUnique } = calculateDuplicateInfo(script.value, 0);
                                    this.closest(".specialized-object").querySelector("[data-field-name=generation_size]").value = maxUnique;
                                    forceNonDuplicatedOutput = true;
                                    generationAmount.value = Math.min(maxUnique, 100_000);
                                }
                            })
                        }
                    ]
                })
            )
        ),
        el.state.listening([script, generationAmount], () => {



            const { effectivePermutations, probability } = calculateDuplicateInfo(script.value, generationAmount.value);

            const percent = cond(
                [forceNonDuplicatedOutput, "0%"],
                [probability > 0 && probability < 0.001, "< 0.1%"],
                [true, `${(probability * 100).toFixed(1)}%`]
            );

            return Section(
                MutedText(
                    "≈ ", InlineCode(Math.round(effectivePermutations).toLocaleString("en-US")),
                    " effective permutations · ",
                    InlineCode(percent), " duplicate chance"
                )
            );
        })
    )
}
